import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Calculator, 
  DollarSign, 
  Wrench,
  ChevronDown,
  ChevronUp,
  Info,
  Plus,
  Trash2,
  Save,
  PenLine,
  Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONDITION_OPTIONS = [
  { value: "good", label: "Good Condition", multiplier: "0.7x", description: "Minor cosmetic updates needed" },
  { value: "average", label: "Average Condition", multiplier: "1.0x", description: "Standard renovation scope" },
  { value: "poor", label: "Poor Condition", multiplier: "1.4x", description: "Major repairs required" }
];

const MANUAL_CATEGORIES = [
  {
    id: "kitchen",
    name: "Kitchen",
    defaults: [
      { name: "Cabinets", cost: 0 },
      { name: "Countertops", cost: 0 },
      { name: "Appliances", cost: 0 },
      { name: "Backsplash", cost: 0 },
      { name: "Sink & Faucet", cost: 0 },
    ]
  },
  {
    id: "bathroom",
    name: "Bathroom",
    defaults: [
      { name: "Vanity & Sink", cost: 0 },
      { name: "Toilet", cost: 0 },
      { name: "Tub/Shower", cost: 0 },
      { name: "Tile Work", cost: 0 },
      { name: "Fixtures", cost: 0 },
    ]
  },
  {
    id: "interior",
    name: "Interior",
    defaults: [
      { name: "Flooring", cost: 0 },
      { name: "Interior Paint", cost: 0 },
      { name: "Doors & Trim", cost: 0 },
      { name: "Lighting", cost: 0 },
      { name: "Drywall Repair", cost: 0 },
    ]
  },
  {
    id: "exterior",
    name: "Exterior",
    defaults: [
      { name: "Exterior Paint", cost: 0 },
      { name: "Siding", cost: 0 },
      { name: "Roof", cost: 0 },
      { name: "Windows", cost: 0 },
      { name: "Landscaping", cost: 0 },
      { name: "Driveway", cost: 0 },
    ]
  },
  {
    id: "mechanical",
    name: "Mechanical",
    defaults: [
      { name: "HVAC", cost: 0 },
      { name: "Electrical", cost: 0 },
      { name: "Plumbing", cost: 0 },
      { name: "Water Heater", cost: 0 },
    ]
  },
  {
    id: "structural",
    name: "Structural",
    defaults: [
      { name: "Foundation", cost: 0 },
      { name: "Framing", cost: 0 },
      { name: "Subfloor", cost: 0 },
    ]
  },
  {
    id: "other",
    name: "Other",
    defaults: [
      { name: "Permits", cost: 0 },
      { name: "Dumpster", cost: 0 },
      { name: "Contingency", cost: 0 },
    ]
  },
];

export default function RehabCalculator({ deal, onEstimateUpdate }) {
  const [mode, setMode] = useState("quick"); // "quick" or "manual"
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [condition, setCondition] = useState("average");
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [saving, setSaving] = useState(false);

  // Manual mode state - categories with line items
  const [manualItems, setManualItems] = useState(() =>
    MANUAL_CATEGORIES.map(cat => ({
      ...cat,
      items: cat.defaults.map((d, i) => ({ ...d, id: `${cat.id}_${i}` })),
      collapsed: true,
    }))
  );

  useEffect(() => { fetchCategories(); }, []);

  // Load saved manual items on mount
  useEffect(() => {
    if (deal?.deal_id) loadManualItems();
  }, [deal?.deal_id]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/rehab/categories`, { withCredentials: true });
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const loadManualItems = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/rehab/manual-estimate/${deal.deal_id}`, { withCredentials: true });
      if (res.data.items?.length > 0) {
        // Merge saved items back into categories
        const saved = res.data.items;
        setManualItems(prev => prev.map(cat => {
          const catItems = saved.filter(s => s.category === cat.id);
          if (catItems.length > 0) {
            return { ...cat, items: catItems.map((s, i) => ({ ...s, id: `${cat.id}_saved_${i}` })) };
          }
          return cat;
        }));
      }
    } catch {
      // No saved data, use defaults
    }
  };

  const toggleCategory = (catId) => {
    setSelectedCategories(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  const calculateEstimate = async () => {
    if (selectedCategories.length === 0) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/rehab/estimate`,
        { categories: selectedCategories, condition, sqft: deal?.sqft || 0 },
        { withCredentials: true }
      );
      setEstimate(res.data);
      if (onEstimateUpdate) onEstimateUpdate(res.data.total_mid);
    } catch (error) {
      console.error("Failed to calculate estimate:", error);
    } finally {
      setLoading(false);
    }
  };

  // Manual mode handlers
  const updateItemCost = (catId, itemId, cost) => {
    setManualItems(prev => prev.map(cat =>
      cat.id === catId
        ? { ...cat, items: cat.items.map(item => item.id === itemId ? { ...item, cost: parseFloat(cost) || 0 } : item) }
        : cat
    ));
  };

  const updateItemName = (catId, itemId, name) => {
    setManualItems(prev => prev.map(cat =>
      cat.id === catId
        ? { ...cat, items: cat.items.map(item => item.id === itemId ? { ...item, name } : item) }
        : cat
    ));
  };

  const addItem = (catId) => {
    setManualItems(prev => prev.map(cat =>
      cat.id === catId
        ? { ...cat, items: [...cat.items, { id: `${catId}_${Date.now()}`, name: "", cost: 0 }] }
        : cat
    ));
  };

  const removeItem = (catId, itemId) => {
    setManualItems(prev => prev.map(cat =>
      cat.id === catId
        ? { ...cat, items: cat.items.filter(item => item.id !== itemId) }
        : cat
    ));
  };

  const toggleCatCollapse = (catId) => {
    setManualItems(prev => prev.map(cat =>
      cat.id === catId ? { ...cat, collapsed: !cat.collapsed } : cat
    ));
  };

  const manualTotal = manualItems.reduce((sum, cat) =>
    sum + cat.items.reduce((s, item) => s + (item.cost || 0), 0), 0
  );

  const getCatTotal = (cat) => cat.items.reduce((s, item) => s + (item.cost || 0), 0);

  const saveManualEstimate = async () => {
    if (!deal?.deal_id) return;
    setSaving(true);
    try {
      const flatItems = manualItems.flatMap(cat =>
        cat.items.filter(i => i.name && i.cost > 0).map(i => ({
          category: cat.id,
          category_name: cat.name,
          name: i.name,
          cost: i.cost,
        }))
      );
      await axios.post(`${BACKEND_URL}/api/rehab/manual-estimate`, {
        deal_id: deal.deal_id,
        items: flatItems,
      }, { withCredentials: true });
      if (onEstimateUpdate) onEstimateUpdate(manualTotal);
    } catch (error) {
      console.error("Failed to save:", error);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]" data-testid="rehab-calculator">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-white/[0.06]/50 transition-all duration-300"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gold/10 flex items-center justify-center">
            <Wrench className="w-5 h-5 text-gold" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-white">Rehab Calculator</h3>
            <p className="text-xs text-white/70">Estimate repair costs</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {(estimate || manualTotal > 0) && (
            <span className="text-gold font-bold">{formatCurrency(mode === "manual" ? manualTotal : (estimate?.total_mid || 0))}</span>
          )}
          {expanded ? <ChevronUp className="w-5 h-5 text-white/70" /> : <ChevronDown className="w-5 h-5 text-white/70" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-white/[0.08]">
          {/* Mode Tabs */}
          <div className="flex border-b border-white/[0.08]">
            <button
              onClick={() => setMode("quick")}
              data-testid="rehab-quick-tab"
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-all ${
                mode === "quick" ? "text-gold border-b-2 border-gold bg-gold/5" : "text-white/50 hover:text-white/70"
              }`}
            >
              <Zap className="w-4 h-4" /> Quick Quote
            </button>
            <button
              onClick={() => setMode("manual")}
              data-testid="rehab-manual-tab"
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-all ${
                mode === "manual" ? "text-gold border-b-2 border-gold bg-gold/5" : "text-white/50 hover:text-white/70"
              }`}
            >
              <PenLine className="w-4 h-4" /> Manual Entry
            </button>
          </div>

          {/* Quick Quote Mode */}
          {mode === "quick" && (
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs text-white/70 uppercase font-bold mb-2 block">Property Condition</label>
                <div className="grid grid-cols-3 gap-2">
                  {CONDITION_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setCondition(opt.value)}
                      className={`p-3 border text-left transition-all ${
                        condition === opt.value ? "border-gold bg-gold/10" : "border-white/[0.08] hover:border-white/[0.1]"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${condition === opt.value ? "text-gold" : "text-white"}`}>{opt.label}</span>
                        <span className={`text-xs ${condition === opt.value ? "text-gold" : "text-white/70"}`}>{opt.multiplier}</span>
                      </div>
                      <p className="text-xs text-white/70">{opt.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-white/70 uppercase font-bold mb-2 block">
                  Select Work Needed ({selectedCategories.length} selected)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.id)}
                      className={`p-3 border text-left transition-all ${
                        selectedCategories.includes(cat.id) ? "border-emerald-500 bg-emerald-500/10" : "border-white/[0.08] hover:border-white/[0.1]"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 border flex items-center justify-center ${
                          selectedCategories.includes(cat.id) ? "border-emerald-500 bg-emerald-500" : "border-white/[0.1]"
                        }`}>
                          {selectedCategories.includes(cat.id) && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className={`text-sm ${selectedCategories.includes(cat.id) ? "text-[#00FF88]" : "text-white/70"}`}>{cat.name}</span>
                      </div>
                      <div className="mt-1 text-xs text-white/70">{formatCurrency(cat.default_min)} - {formatCurrency(cat.default_max)}</div>
                    </button>
                  ))}
                </div>
              </div>

              <Button onClick={calculateEstimate} disabled={selectedCategories.length === 0 || loading} className="w-full gold-gradient-btn font-bold">
                <Calculator className="w-4 h-4 mr-2" />
                {loading ? "Calculating..." : "Calculate Estimate"}
              </Button>

              {estimate && (
                <div className="bg-black border border-white/[0.08] p-4 space-y-3">
                  <div className="flex items-center justify-between border-b border-white/[0.08] pb-3">
                    <span className="text-white/70">Estimated Rehab Cost</span>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gold">{formatCurrency(estimate.total_mid)}</div>
                      <div className="text-xs text-white/70">Range: {formatCurrency(estimate.total_min)} - {formatCurrency(estimate.total_max)}</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-xs text-white/70 uppercase font-bold">Breakdown</div>
                    {estimate.items.map((item) => (
                      <div key={item.category_id} className="flex items-center justify-between text-sm">
                        <span className="text-white/70">{item.category_name}</span>
                        <span className="text-white/70">{formatCurrency(item.estimate_mid)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-start gap-2 pt-2 border-t border-white/[0.08]">
                    <Info className="w-4 h-4 text-white/70 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-white/70">
                      Estimates based on {estimate.condition} condition ({estimate.condition_multiplier}x multiplier).
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manual Entry Mode */}
          {mode === "manual" && (
            <div className="p-4 space-y-3">
              <p className="text-xs text-white/40">Enter actual costs per line item. Only items with a cost will be saved.</p>

              {manualItems.map((cat) => {
                const catTotal = getCatTotal(cat);
                return (
                  <div key={cat.id} className="border border-white/[0.06] rounded-lg overflow-hidden" data-testid={`manual-cat-${cat.id}`}>
                    <button
                      onClick={() => toggleCatCollapse(cat.id)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.02] hover:bg-white/[0.04] transition-all"
                    >
                      <span className="text-sm font-semibold text-white">{cat.name}</span>
                      <div className="flex items-center gap-3">
                        {catTotal > 0 && <span className="text-gold font-mono text-sm font-bold">{formatCurrency(catTotal)}</span>}
                        {cat.collapsed ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronUp className="w-4 h-4 text-white/40" />}
                      </div>
                    </button>

                    {!cat.collapsed && (
                      <div className="px-4 py-3 space-y-2 border-t border-white/[0.06]">
                        {cat.items.map((item) => (
                          <div key={item.id} className="flex items-center gap-2">
                            <Input
                              value={item.name}
                              onChange={(e) => updateItemName(cat.id, item.id, e.target.value)}
                              placeholder="Item name"
                              className="flex-1 h-9 bg-black border-white/[0.08] text-white text-sm"
                            />
                            <div className="relative w-32 flex-shrink-0">
                              <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                              <Input
                                type="number"
                                value={item.cost || ""}
                                onChange={(e) => updateItemCost(cat.id, item.id, e.target.value)}
                                placeholder="0"
                                className="h-9 pl-7 bg-black border-white/[0.08] text-white text-sm font-mono"
                                data-testid={`manual-cost-${item.id}`}
                              />
                            </div>
                            <button
                              onClick={() => removeItem(cat.id, item.id)}
                              className="p-1.5 text-white/20 hover:text-red-400 transition-colors flex-shrink-0"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => addItem(cat.id)}
                          className="flex items-center gap-1.5 text-xs text-white/40 hover:text-gold transition-colors py-1"
                        >
                          <Plus className="w-3 h-3" /> Add line item
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Manual Total + Save */}
              <div className="bg-black border border-gold/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white/70 text-sm font-semibold">Total Rehab Estimate</span>
                  <span className="text-gold font-black text-2xl font-mono">{formatCurrency(manualTotal)}</span>
                </div>
                <Button
                  onClick={saveManualEstimate}
                  disabled={saving || manualTotal === 0}
                  className="w-full gold-gradient-btn font-bold"
                  data-testid="save-manual-rehab-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save to Deal"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
